import {
  cycleAffected,
  cycleInfo,
  cycleOn,
  hormonal,
  nextPeriodEstimate,
  ovulationEstimate,
  periodDay,
  phaseOf,
  prePeriodDay,
  prePeriodWindow,
  type PhaseId,
} from "./cycle";
import { addDays, dayLabel, dayMonth, daysBetween, parseDay } from "./date";
import { basedOn, type Insight } from "./insight";
import { sessionKeyFor, weekMapFor } from "./plan";
import { dayFull } from "./stats";
import { SYMPTOM_ROWS, choice, hasFlow, intensity, symptomsOf, type SymptomRowId } from "./symptoms";
import type { KayaState } from "./types";

/**
 * What the Cycle page and the cycle clauses elsewhere say (ADR-033, research §2.7 C1–C8, R2).
 *
 * Everything here reads logged starts up to `today` — a start typed in for a future date is not
 * history yet. Estimates come from `cycle.ts`; this module only aggregates. Callers pass the
 * figures that belong to other chapters (weights, intake, deficit, active minutes) as callbacks
 * and numbers, so this module does not decide how a weigh-in or a day's In is read.
 */

/** How far back the by-phase comparisons look: three cycles and a little. */
const LOOKBACK = 90;

const NUM_WORDS = ["no", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten"];
const word = (n: number) => NUM_WORDS[n] ?? String(n);
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const mean = (xs: number[]) => xs.reduce((a, b) => a + b, 0) / xs.length;
const round10 = (x: number) => Math.round(x / 10) * 10;

/** Logged starts on or before `today`, oldest first. */
export function loggedStarts(S: KayaState, today: string): string[] {
  return (S.cycle.starts || []).filter((st) => st <= today).sort();
}

/**
 * How long the period that began on `st` lasted: the logged end when there is one, else the run
 * of days from `st` with flow logged, when that run says more than the start alone. Null when
 * neither is known — the caller falls back to the set period length.
 */
export function periodLengthOf(S: KayaState, st: string, next?: string): number | null {
  const end = S.cycle.ends?.[st];
  if (end) return daysBetween(st, end) + 1;
  let run = 0;
  for (let ds = st; run < 15 && (!next || ds < next) && hasFlow(symptomsOf(S, ds)); ds = addDays(ds, 1)) run++;
  return run >= 2 ? run : null;
}

/* ---------- C2 regularity ---------- */

export type RegularityVerdict = "regular" | "varies" | "outside" | "hormonal";

export interface Regularity {
  periods: number;
  /** the last three cycle lengths, oldest first */
  last: number[];
  avgCycle: number;
  /** false while the average is still the set length */
  avgFromLog: boolean;
  avgPeriod: number;
  /** longest minus shortest of `last` */
  spread: number | null;
  /** null under two complete cycles */
  verdict: RegularityVerdict | null;
}

/**
 * "Regular" when the last three cycles are within 2 days of each other and all in 21–35. On a
 * hormonal method the bleed comes when the method schedules it (R6), so 21–35 is not a verdict on
 * it at all — not even "outside" — and the caller shows a different sentence for "hormonal".
 */
export function regularity(S: KayaState, today: string): Regularity {
  const starts = loggedStarts(S, today);
  const gaps: number[] = [];
  for (let i = 1; i < starts.length; i++) gaps.push(daysBetween(starts[i - 1], starts[i]));
  const last = gaps.slice(-3);
  const lens = starts.map((st, i) => periodLengthOf(S, st, starts[i + 1])).filter((x): x is number => x !== null);
  const spread = last.length ? Math.max(...last) - Math.min(...last) : null;
  let verdict: RegularityVerdict | null = null;
  if (hormonal(S)) {
    verdict = "hormonal";
  } else if (last.length >= 2) {
    if (last.some((g) => g < 21 || g > 35)) verdict = "outside";
    else verdict = spread! <= 2 ? "regular" : "varies";
  }
  return {
    periods: starts.length,
    last,
    avgCycle: gaps.length ? Math.round(mean(gaps)) : S.cycle.length || 28,
    avgFromLog: gaps.length > 0,
    avgPeriod: lens.length ? Math.round(mean(lens)) : S.cycle.periodLen || 5,
    spread,
    verdict,
  };
}

/* ---------- C3 the scale and the cycle ---------- */

export interface WaterSwing {
  /** mean of the 4 days before a start less mean of cycle days 4–10, averaged over cycles */
  kg: number;
  cycles: number;
  /** weigh-ins that fall in the cycle-affected window */
  tagged: number;
  readings: number;
}

/**
 * The person's own pre-period water swing. Needs two cycles each with a reading before the start
 * and one on days 4–10; until then null, and the page uses the population range instead.
 */
export function waterSwing(
  S: KayaState,
  today: string,
  weights: (ds: string) => number | null
): WaterSwing | null {
  if (!cycleOn(S)) return null;
  const starts = loggedStarts(S, today);
  if (!starts.length) return null;
  const read = (from: string, to: string) => {
    const out: number[] = [];
    for (let ds = from; ds <= to && ds <= today; ds = addDays(ds, 1)) {
      const w = weights(ds);
      if (w !== null) out.push(w);
    }
    return out;
  };
  const diffs: number[] = [];
  for (const st of starts) {
    const before = read(addDays(st, -4), addDays(st, -1));
    const after = read(addDays(st, 3), addDays(st, 9));
    if (before.length && after.length) diffs.push(mean(before) - mean(after));
  }
  let tagged = 0;
  let readings = 0;
  for (let ds = addDays(starts[0], -5); ds <= today; ds = addDays(ds, 1)) {
    if (weights(ds) === null) continue;
    readings++;
    if (cycleAffected(S, ds)) tagged++;
  }
  if (diffs.length < 2) return null;
  return { kg: Math.round(mean(diffs) * 10) / 10, cycles: diffs.length, tagged, readings };
}

/* ---------- F12 appetite by phase ---------- */

export interface IntakeByPhase {
  /** mean In on logged days in a pre-period week */
  pre: number;
  other: number;
  diff: number;
  preDays: number;
  otherDays: number;
  /** what the cravings chips said in those weeks, when they said anything */
  cravings: "sweet" | "salty" | null;
}

/**
 * Mean In in pre-period weeks against other logged days. Null under 7 logged days of each.
 *
 * Over the whole record and ending yesterday, the window Progress › Food's `food.cycle` uses, so
 * the Cycle and Food pages never show two different "kcal more before a period" figures; a month
 * holds one pre-period week at most, and today is still being eaten.
 */
export function intakeByPhase(
  S: KayaState,
  today: string,
  intake: (ds: string) => number | null
): IntakeByPhase | null {
  const starts = loggedStarts(S, today);
  if (!cycleOn(S) || !starts.length) return null;
  const pre: number[] = [];
  const other: number[] = [];
  let sweet = 0;
  let salty = 0;
  const plan = (S.profile.startDate || "").slice(0, 10);
  let from = addDays(starts[0], -7);
  if (plan && plan < from) from = plan;
  // bounded, so a start typed in years back cannot make this walk thousands of days
  const floor = addDays(today, -730);
  if (from < floor) from = floor;
  for (let ds = from; ds < today; ds = addDays(ds, 1)) {
    const inPre = prePeriodDay(S, ds);
    if (inPre) {
      const keys = symptomsOf(S, ds);
      if (keys.includes("cravings:sweet")) sweet++;
      if (keys.includes("cravings:salty")) salty++;
    }
    const k = intake(ds);
    if (k === null) continue;
    (inPre ? pre : other).push(k);
  }
  if (pre.length < 7 || other.length < 7) return null;
  const p = mean(pre);
  const o = mean(other);
  return {
    pre: Math.round(p),
    other: Math.round(o),
    diff: Math.round(p - o),
    preDays: pre.length,
    otherDays: other.length,
    cravings: sweet > salty ? "sweet" : salty > sweet ? "salty" : null,
  };
}

/** The widest run of cycle days the symptom insight still calls a peak. */
const PEAK_MAX_DAYS = 7;

/* ---------- C6 energy by phase ---------- */

/**
 * The phase of a day already lived. Inside a completed cycle the next logged start is known, so
 * ovulation is counted back 14 days from it rather than forward from the set length.
 */
function pastPhase(S: KayaState, ds: string, starts: string[]): PhaseId | null {
  if (hormonal(S)) return null;
  let prev: string | null = null;
  let next: string | null = null;
  for (const st of starts) {
    if (st <= ds) prev = st;
    else if (!next) next = st;
  }
  if (!prev) return null;
  if (!next) return phaseOf(S, ds);
  const len = daysBetween(prev, next);
  const day = daysBetween(prev, ds) + 1;
  const ov = len - 14;
  const PL = periodLengthOf(S, prev, next) ?? (S.cycle.periodLen || 5);
  if (day <= PL) return "menstrual";
  if (day < ov - 1) return "follicular";
  if (day <= ov + 1) return "ovulation";
  return "luteal";
}

export interface EnergyByPhase {
  luteal: { low: number; of: number };
  follicular: { low: number; of: number };
  cycles: number;
}

/**
 * Of the days with anything logged on the sheet, the share logged low energy (`energy:0` or the
 * old `lowenergy` chip), luteal against follicular. A day logged "fine" counts as not low. Null
 * on a hormonal method (no phases) or under three logged days in either phase.
 */
export function energyByPhase(S: KayaState, today: string): EnergyByPhase | null {
  if (!cycleOn(S) || hormonal(S)) return null;
  const starts = loggedStarts(S, today);
  const lut = { low: 0, of: 0 };
  const fol = { low: 0, of: 0 };
  const cycles = new Set<string>();
  for (let i = LOOKBACK - 1; i >= 0; i--) {
    const ds = addDays(today, -i);
    const keys = symptomsOf(S, ds);
    if (!keys.length) continue;
    const ph = pastPhase(S, ds, starts);
    const bucket = ph === "luteal" ? lut : ph === "follicular" ? fol : null;
    if (!bucket) continue;
    bucket.of++;
    if (choice(keys, "energy") === "0") bucket.low++;
    const st = starts.filter((s) => s <= ds).pop();
    if (st) cycles.add(st);
  }
  if (lut.of < 3 || fol.of < 3) return null;
  return { luteal: lut, follicular: fol, cycles: cycles.size };
}

/* ---------- C5 symptom map ---------- */

export interface SymptomMapRow {
  id: SymptomRowId;
  label: string;
  /** index 0 is cycle day 1; 0..3 averaged over the cycles that logged that day, null if none */
  cells: (number | null)[];
}

export interface SymptomMap {
  len: number;
  /** cycles with at least one day logged */
  cycles: number;
  /** estimated, so shown as "est."; null on a hormonal method */
  ovulationDay: number | null;
  todayDay: number | null;
  rows: SymptomMapRow[];
}

/**
 * Symptoms by cycle day over the last `cycles` cycles. A day logged at all counts as 0 for the
 * rows it did not mention, so a single "fine" pulls a day's average down rather than leaving it
 * blank. Only rows that were ever above 0 are returned.
 */
export function symptomMap(S: KayaState, today: string, cycles = 3): SymptomMap | null {
  if (!cycleOn(S)) return null;
  const starts = loggedStarts(S, today);
  if (!starts.length) return null;
  const len = regularity(S, today).avgCycle;
  const sum: Record<SymptomRowId, number[]> = {} as Record<SymptomRowId, number[]>;
  const cnt: Record<SymptomRowId, number[]> = {} as Record<SymptomRowId, number[]>;
  for (const r of SYMPTOM_ROWS) {
    sum[r.id] = new Array<number>(len).fill(0);
    cnt[r.id] = new Array<number>(len).fill(0);
  }
  let used = 0;
  const from = Math.max(0, starts.length - cycles);
  for (let c = from; c < starts.length; c++) {
    const st = starts[c];
    const next = starts[c + 1];
    let any = false;
    for (let i = 0; i < len; i++) {
      const ds = addDays(st, i);
      if (ds > today || (next && ds >= next)) break;
      const keys = symptomsOf(S, ds);
      if (!keys.length) continue;
      any = true;
      for (const r of SYMPTOM_ROWS) {
        sum[r.id][i] += intensity(keys, r.id) ?? 0;
        cnt[r.id][i]++;
      }
    }
    if (any) used++;
  }
  const rows = SYMPTOM_ROWS.map((r) => ({
    id: r.id,
    label: r.label,
    cells: sum[r.id].map((s, i) => (cnt[r.id][i] ? Math.round((s / cnt[r.id][i]) * 10) / 10 : null)),
  })).filter((r) => r.cells.some((v) => v !== null && v > 0));
  const td = daysBetween(starts[starts.length - 1], today) + 1;
  return {
    len,
    cycles: used,
    ovulationDay: hormonal(S) ? null : len - 14,
    todayDay: td >= 1 && td <= len ? td : null,
    rows,
  };
}

/* ---------- C4 period-day training ---------- */

export interface PeriodTrainingTally {
  from: string;
  to: string;
  days: number;
  /** the gentle session ("P") finished */
  gentle: number;
  /** "rest that keeps the streak" (`periodSkip`) */
  rested: number;
  /** a cycle care session ("Y" in `extras`) */
  care: number;
  /** a full session other than the gentle one */
  full: number;
}

export interface PeriodTraining {
  /** the latest logged period */
  last: PeriodTrainingTally;
  all: Omit<PeriodTrainingTally, "from" | "to"> & { periods: number };
}

/**
 * What was done on logged period days. A period day is `periodDay` (a logged start through its
 * logged end or the set period length); a predicted day is not one. "Gentle" is the day's
 * session — `sessionKey`, else what the plan scheduled, which is "P" on a period day unless it
 * was swapped in `overrides` — being "P" and finished; "rested" is `periodSkip`; cycle care is
 * "Y" in `extras` — the same three markers `cycleStats` counts (ADR-019, ADR-022).
 */
export function periodTraining(S: KayaState, today: string): PeriodTraining | null {
  if (!cycleOn(S)) return null;
  const starts = loggedStarts(S, today);
  const tallies: PeriodTrainingTally[] = [];
  for (const st of starts) {
    const t: PeriodTrainingTally = { from: st, to: st, days: 0, gentle: 0, rested: 0, care: 0, full: 0 };
    for (let ds = st; ds <= today && periodDay(S, ds) && t.days < 15; ds = addDays(ds, 1)) {
      t.days++;
      t.to = ds;
      const L = S.logs[ds];
      if (!L) continue;
      const key = L.sessionKey || sessionKeyFor(S, ds);
      const full = dayFull(S, ds);
      if (key === "P" && full) t.gentle++;
      else if (full) t.full++;
      if (L.periodSkip) t.rested++;
      if ((L.extras || []).includes("Y")) t.care++;
    }
    if (t.days) tallies.push(t);
  }
  if (!tallies.length) return null;
  const all = { days: 0, gentle: 0, rested: 0, care: 0, full: 0, periods: tallies.length };
  for (const t of tallies) {
    all.days += t.days;
    all.gentle += t.gentle;
    all.rested += t.rested;
    all.care += t.care;
    all.full += t.full;
  }
  return { last: tallies[tallies.length - 1], all };
}

/* ---------- C4b pain days and sessions (ADR-057) ---------- */

export interface PainTraining {
  /** days with pain logged in the window, oldest first */
  days: string[];
  /** the gentle session finished */
  gentle: number;
  /** a full session other than the gentle one */
  own: number;
  /** a scheduled rest day, or a period day kept as rest */
  rested: string[];
  /** the logged periods those days fall in */
  periods: number;
}

/**
 * What she did on the days she logged pain — her own pattern beside the rule that offers the
 * gentle session on them, which is what McNulty 2020 recommends over phase rules. Null under two
 * pain days: one day is not a pattern.
 */
export function painTraining(S: KayaState, today: string): PainTraining | null {
  if (!cycleOn(S)) return null;
  const starts = loggedStarts(S, today);
  const out: PainTraining = { days: [], gentle: 0, own: 0, rested: [], periods: 0 };
  const periods = new Set<string>();
  for (let i = LOOKBACK - 1; i >= 0; i--) {
    const ds = addDays(today, -i);
    const keys = symptomsOf(S, ds);
    const pain = choice(keys, "pain");
    if (!(pain !== null ? pain !== "0" : keys.includes("cramps"))) continue;
    out.days.push(ds);
    const st = starts.filter((x) => x <= ds).pop();
    if (st) periods.add(st);
    const L = S.logs[ds];
    const key = L?.sessionKey || sessionKeyFor(S, ds);
    const full = dayFull(S, ds);
    if (key === "P" && full) out.gentle++;
    else if (full) out.own++;
    else if (L?.periodSkip || key === "R" || weekMapFor(S)[parseDay(ds).getDay()] === "R") out.rested.push(ds);
  }
  out.periods = periods.size;
  return out.days.length >= 2 ? out : null;
}

const WEEKDAY = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const ORDINAL = ["", "first", "second", "third", "fourth", "fifth", "sixth", "seventh", "eighth", "ninth", "tenth"];

/** "14–15 Aug and 12–13 Sep": runs of consecutive days, the last three. */
function runsLabel(days: string[]): string {
  const runs: [string, string][] = [];
  for (const d of days) {
    const last = runs[runs.length - 1];
    if (last && addDays(last[1], 1) === d) last[1] = d;
    else runs.push([d, d]);
  }
  const words = runs.slice(-3).map(([a, b]) => {
    if (a === b) return dayMonth(a);
    const same = parseDay(a).getMonth() === parseDay(b).getMonth();
    return `${same ? parseDay(a).getDate() : dayMonth(a)}–${dayMonth(b)}`;
  });
  return words.length > 1 ? `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}` : words[0];
}

/* ---------- C8 signposts ---------- */

export interface Signpost {
  key: "cycle-length" | "period-length" | "heavy" | "soaked";
  text: string;
}

const DOCTOR = "worth mentioning to a doctor";

/**
 * Quiet, factual lines — what was logged and that it is worth mentioning to a doctor, never a
 * condition's name. Empty when nothing is outside the usual ranges.
 */
export function signposts(S: KayaState, today: string): Signpost[] {
  if (!cycleOn(S)) return [];
  const out: Signpost[] = [];
  const starts = loggedStarts(S, today);
  const reg = regularity(S, today);
  const lastTwo = reg.last.slice(-2);
  // On a hormonal method the bleed comes when the method schedules it — an extended pill runs
  // 84 days by design — so its spacing is no signpost. Length, heaviness and soaking still are.
  const out21 = (g: number) => !hormonal(S) && (g < 21 || g > 35);
  const latest = lastTwo[lastTwo.length - 1];
  if (lastTwo.length === 2 && lastTwo.every(out21))
    out.push({
      key: "cycle-length",
      text: `Your last two cycles were ${lastTwo[0]} and ${lastTwo[1]} days, outside the usual 21–35. That is ${DOCTOR}, especially if it is new.`,
    });
  else if (latest !== undefined && out21(latest))
    out.push({
      key: "cycle-length",
      text: `Your last cycle was ${latest} days, outside the usual 21–35. If the next one is too, it is ${DOCTOR}.`,
    });

  if (starts.length) {
    const st = starts[starts.length - 1];
    const len = periodLengthOf(S, st) ?? S.cycle.periodLen ?? 5;
    if (len > 7)
      out.push({
        key: "period-length",
        text: `Your ${periodLengthOf(S, st) ? "last period lasted" : "period length is set to"} ${len} days. Periods over 7 days are ${DOCTOR} — they can lower iron.`,
      });
    let heavy = 0;
    for (let ds = st; ds <= today && daysBetween(st, ds) < 10; ds = addDays(ds, 1))
      if (choice(symptomsOf(S, ds), "flow") === "heavy") heavy++;
    if (heavy >= 3)
      out.push({
        key: "heavy",
        text: `Heavy flow was logged on ${heavy} days of your last period. That is ${DOCTOR} — heavy periods can lower iron.`,
      });
  }

  let soaked = 0;
  for (let i = 0; i < LOOKBACK; i++) if (symptomsOf(S, addDays(today, -i)).includes("soaked")) soaked++;
  if (soaked)
    out.push({
      key: "soaked",
      text: `You logged soaking through in under 2 hours on ${plural(soaked, "day")}. That is ${DOCTOR}.`,
    });
  return out;
}

/* ---------- R2 energy availability ---------- */

export interface RedS {
  late: number;
  deficit: number;
  activeMin: number;
  text: string;
}

/** Days past the expected start, from the last logged start and the logged average; ≤ 0 on time. */
export function daysLate(S: KayaState, today: string): number | null {
  const starts = loggedStarts(S, today);
  if (!starts.length) return null;
  const expected = addDays(starts[starts.length - 1], regularity(S, today).avgCycle);
  return daysBetween(expected, today);
}

/**
 * The RED-S signal: quiet unless a period is at least 10 days late AND the 28-day deficit is at
 * least 500 a day AND active time is at least 300 minutes a week. Any one alone is ordinary.
 * Not on a hormonal method, where the bleed's timing is the method's.
 */
export function redS(
  S: KayaState,
  today: string,
  deficit: number | null,
  activeMin: number | null
): RedS | null {
  if (!cycleOn(S) || hormonal(S)) return null;
  const late = daysLate(S, today);
  if (late === null || late < 10 || deficit === null || deficit > -500 || activeMin === null || activeMin < 300)
    return null;
  const hours = Math.round(activeMin / 60);
  return {
    late,
    deficit,
    activeMin,
    text: `Your period is ${late} days later than usual, you are eating ~${round10(-deficit)} kcal a day below your burn and active about ${hours} hours a week. Low energy can delay periods — consider easing the deficit, and it is ${DOCTOR}.`,
  };
}

/* ---------- the insights ---------- */

export interface CycleDeps {
  weights: (ds: string) => number | null;
  intake: (ds: string) => number | null;
  /** 28-day mean of In less Out, kcal/day; negative is a deficit */
  deficit: number | null;
  /** active minutes a week */
  activeMin: number | null;
}

function periodsWord(n: number): string {
  return plural(n, "logged period");
}

/** "Sat 12 – Wed 16 Sep" */
function rangeLabel(from: string, to: string): string {
  if (from === to) return dayLabel(from);
  const a = parseDay(from);
  const b = parseDay(to);
  const head = a.getMonth() === b.getMonth() ? dayLabel(from).replace(/ \w+$/, "") : dayLabel(from);
  return `${head} – ${dayLabel(to)}`;
}

function kgWord(kg: number): string {
  const r = Math.round(kg * 2) / 2;
  return Number.isInteger(r) ? String(r) : r.toFixed(1);
}

const PHASE_WORD: Record<PhaseId, string> = {
  menstrual: "period",
  follicular: "follicular",
  ovulation: "ovulation",
  luteal: "luteal",
};

/**
 * Every cycle sentence the Cycle page can show, in its order. Empty with tracking off — nothing
 * cycle-related renders then (ADR-033 decision 5). A claim the data has not earned yet comes back
 * with `wait` and a `text` that says what unlocks it.
 */
export function cycleInsights(S: KayaState, today: string, deps: CycleDeps): Insight[] {
  if (!cycleOn(S)) return [];
  const out: Insight[] = [];
  const starts = loggedStarts(S, today);
  const n = starts.length;
  const reg = regularity(S, today);
  const horm = hormonal(S);
  const periods = periodsWord(n);

  // C1 — where she is now
  const info = cycleInfo(S, today);
  if (!n || !info || info.day === null) {
    out.push({
      key: "cycle.now",
      text: "Log a period start and Kaya can place you in your cycle and estimate the next one.",
      basedOn: basedOn("no logged periods yet"),
      wait: "after your first logged period",
    });
  } else {
    const next = nextPeriodEstimate(S, today);
    const ov = ovulationEstimate(S, today);
    const ph = phaseOf(S, today);
    const parts = [`Cycle day ${info.day} of ~${reg.avgCycle}${ph ? ` · ${PHASE_WORD[ph]}` : ""}.`];
    if (ov && ov >= today) parts.push(`Ovulation ~${dayLabel(ov)} est.;`);
    if (next) parts.push(`${horm ? "next bleed" : "next period"} ~${dayLabel(next)} est.`);
    const text = parts.join(" ").replace(/\. next/, ". Next");
    out.push({
      key: "cycle.now",
      text,
      basedOn: basedOn(periods, reg.avgFromLog ? null : "your set cycle length"),
      tag: "est",
    });
  }

  // C7 — the pre-period week
  const win = n ? prePeriodWindow(S, today) : null;
  const water = waterSwing(S, today, deps.weights);
  const appetite = intakeByPhase(S, today, deps.intake);
  if (win) {
    const next = addDays(win.to, 1);
    if (horm) {
      out.push({
        key: "cycle.next",
        text: `Next bleed ~${dayLabel(next)}. Readings in the days around it may run high on water.`,
        basedOn: basedOn(periods),
        tag: "est",
      });
    } else {
      const scale = water && water.kg >= 0.5 ? `about +${kgWord(water.kg)} kg on the scale` : "up to 2 kg of water on the scale";
      const hungry = !appetite || appetite.diff > 50 ? " and more appetite" : "";
      const verb = today >= win.from ? "started" : "starts";
      out.push({
        key: "cycle.next",
        text: `Pre-period week ${verb} ~${dayLabel(win.from)}: expect ${scale}${hungry}. Plan rest for ~${dayMonth(next)}.`,
        basedOn: basedOn(periods, water ? `${water.cycles} full cycles of weigh-ins` : null),
        tag: "est",
      });
    }
  }

  // C2 — regularity. Hormonal comes first: the check never applies to it, at any period count.
  if (reg.verdict === "hormonal") {
    out.push({
      key: "cycle.regular",
      text: "On a hormonal method the bleeds follow the method, not a cycle; Kaya tracks the dates and skips the regularity check.",
      basedOn: basedOn(periods),
    });
  } else if (reg.verdict === null || n < 3) {
    const more = Math.max(1, 3 - n);
    out.push({
      key: "cycle.regular",
      text: "Whether your cycles are regular shows once three periods are logged.",
      basedOn: basedOn(periods),
      wait: `after ${word(more)} more logged period${more === 1 ? "" : "s"}`,
    });
  } else {
    const list = reg.last.join(", ").replace(/, (\d+)$/, " and $1");
    const text =
      reg.verdict === "regular"
        ? `Cycles of 21–35 days are the usual range; yours sit at ${list}. ${cap(word(n))} logged periods is enough to say regular, not yet enough to predict to the day.`
        : reg.verdict === "varies"
          ? `Your last cycles were ${list} days — inside the usual 21–35 but ${reg.spread} days apart, so the next date is a range rather than a day.`
          : `Your last cycles were ${list} days; the usual range is 21–35.`;
    out.push({ key: "cycle.regular", text, basedOn: basedOn(periods) });
  }

  // C3 — the scale. Worded by the swing's size, not its sign: a negative swing is the scale
  // dropping before a period, not "barely moving" — that branch is for a genuinely small swing.
  if (water) {
    const mag = Math.abs(water.kg);
    out.push({
      key: "cycle.water",
      text:
        mag >= 0.3
          ? `Your weight ${water.kg > 0 ? "rises" : "drops"} ~${mag.toFixed(1)} kg in the 4 days before a period and is back by day 4 — water, not fat. Those ${water.tagged} weigh-ins are tagged so the trend ignores them.`
          : `Your weight barely moves around a period (±${mag.toFixed(1)} kg). The ${water.tagged} weigh-ins in that window are still tagged.`,
      basedOn: basedOn(plural(water.readings, "weigh-in"), `${water.cycles} full cycles`),
      tag: "cycle",
      link: { to: "/progress/body", label: "See Body" },
    });
  } else if (n) {
    out.push({
      key: "cycle.water",
      text: "Many women hold 0.5–2 kg of water before a period. Your own figure needs weigh-ins around two full cycles.",
      basedOn: basedOn(periods),
      tag: "cycle",
      wait: "after two cycles with weigh-ins before and after",
      link: { to: "/progress/body", label: "See Body" },
    });
  }

  // F12 — appetite
  if (appetite) {
    const d = round10(appetite.diff);
    const text =
      d > 50
        ? `In the pre-period week you eat ~${d} kcal more a day${appetite.cravings ? `, mostly ${appetite.cravings}` : ""} — normal; the budget can carry it.`
        : d < -50
          ? `In the pre-period week you eat ~${-d} kcal less a day than otherwise.`
          : "Your intake holds steady through the pre-period week.";
    out.push({
      key: "cycle.appetite",
      text,
      basedOn: basedOn(plural(appetite.preDays + appetite.otherDays, "logged day"), periods),
      link: { to: "/progress/food", label: "See Food" },
    });
  } else if (n) {
    out.push({
      key: "cycle.appetite",
      text: "Appetite before a period shows once a week of food is logged in a pre-period week.",
      basedOn: basedOn(periods),
      wait: "after 7 logged days before a period",
      link: { to: "/progress/food", label: "See Food" },
    });
  }

  // C6 — energy
  if (!horm) {
    const en = energyByPhase(S, today);
    if (en) {
      const l = Math.round((en.luteal.low / en.luteal.of) * 100);
      const f = Math.round((en.follicular.low / en.follicular.of) * 100);
      out.push({
        key: "cycle.energy",
        text:
          l - f >= 15
            ? `Energy was logged low on ${l} % of luteal days vs ${f} % of follicular days — your own pattern, not a rule. On a day you log low energy, Kaya offers a shorter session, whatever the cycle day.`
            : `Energy was logged low about as often in both halves of your cycle (${l} % luteal, ${f} % follicular).`,
        basedOn: basedOn(plural(en.luteal.of + en.follicular.of, "logged day"), plural(en.cycles, "cycle")),
        link: { to: "/progress/training", label: "See Training" },
      });
    } else if (n) {
      out.push({
        key: "cycle.energy",
        text: "How your energy moves with your cycle shows once it is logged on a few days in each half.",
        basedOn: basedOn(periods),
        wait: "after 3 logged days in each half of the cycle",
        link: { to: "/progress/training", label: "See Training" },
      });
    }
  }

  // C5 — symptoms
  const map = symptomMap(S, today);
  if (map && map.rows.length) {
    if (map.cycles < 3) {
      const more = 3 - map.cycles;
      out.push({
        key: "cycle.symptoms",
        text: "Patterns — which days symptoms peak, and whether they ease in more active cycles — show after three cycles of symptoms.",
        basedOn: basedOn(`${plural(map.cycles, "cycle")} of symptoms so far`),
        wait: `after ${word(more)} more cycle${more === 1 ? "" : "s"}`,
      });
    } else {
      const top = map.rows
        .filter((r) => r.id !== "flow")
        .map((r) => {
          const peak = Math.max(...r.cells.map((v) => v ?? 0));
          const days = r.cells.map((v, i) => (v !== null && v >= Math.max(1, peak * 0.66) ? i + 1 : 0)).filter(Boolean);
          return { r, peak, days };
        })
        .filter((x) => x.peak >= 1 && x.days.length)
        .sort((a, b) => b.peak - a.peak)[0];
      const name = top ? cap(top.r.id === "energy" ? "low energy" : top.r.label) : "";
      // A run wider than a week is most of the cycle, not a peak: say so rather than "days 1–24".
      const wide = !!top && top.days[top.days.length - 1] - top.days[0] + 1 > PEAK_MAX_DAYS;
      out.push({
        key: "cycle.symptoms",
        text: !top
          ? "Symptoms were mild across your logged cycles."
          : wide
            ? `${name} is logged across most of the cycle, not just around the period.`
            : `${name} peaks on day${top.days.length > 1 ? "s" : ""} ${
                top.days.length > 1 ? `${top.days[0]}–${top.days[top.days.length - 1]}` : top.days[0]
              } across your cycles.`,
        basedOn: basedOn(`${plural(map.cycles, "cycle")} of symptoms logged`),
      });
    }
  }

  // C4 — training on period days
  const tr = periodTraining(S, today);
  if (tr) {
    const t = tr.last;
    const rest = t.rested ? " and the rest days kept the streak — that is the plan working, not a lapse" : "";
    out.push({
      key: "cycle.training",
      text:
        t.gentle || t.rested
          ? `Sessions went gentle on ${t.gentle} of ${t.days} period days${rest}.`
          : `${cap(word(t.days))} period days logged; the gentle session is there whenever a full one feels like too much.`,
      basedOn: basedOn(plural(t.days, "period day"), rangeLabel(t.from, t.to)),
    });
  }

  // C4b — pain days: what she did, beside the rule that offers the gentle session (ADR-057)
  const pt = painTraining(S, today);
  if (pt) {
    const d = pt.days.length;
    const rest = pt.rested.length;
    const tail =
      pt.own === 0 && rest === 1 && pt.gentle === d - 1
        ? `; the ${ORDINAL[d] ?? "other"} was your ${WEEKDAY[parseDay(pt.rested[0]).getDay()]} rest`
        : [pt.own ? `your own session on ${pt.own}` : null, rest ? `rested on ${rest}` : null]
            .filter(Boolean)
            .map((x) => `; ${x}`)
            .join("");
    out.push({
      key: "cycle.pain",
      text: `On the ${d} days you logged pain, you did the gentle session on ${pt.gentle}${tail}.`,
      basedOn: basedOn(plural(pt.periods, "period"), `pain logged ${runsLabel(pt.days)}`),
    });
  }

  // C8 — signposts
  if (n) {
    const flags = signposts(S, today);
    out.push({
      key: "cycle.flags",
      text: flags.length ? flags.map((f) => f.text).join(" ") : "Nothing in your log is outside the usual ranges.",
      basedOn: basedOn(periods),
    });
  }

  // R2 — energy availability
  if (n && !horm) {
    const red = redS(S, today, deps.deficit, deps.activeMin);
    const late = daysLate(S, today);
    const losing = deps.deficit !== null && deps.deficit < 0;
    out.push({
      key: "cycle.reds",
      text: red
        ? red.text
        : late !== null && late > 0
          ? `Your period is ${plural(late, "day")} later than your average — one late period is common. A missed period alongside a large deficit and hard training would show here.`
          : `Periods are arriving on time${losing ? " while you lose weight" : ""} — a missed period alongside a large deficit and hard training would show here first.`,
      basedOn: basedOn(periods, deps.deficit !== null ? "28 days of In and Out" : null),
      tag: "est",
    });
  }

  return out;
}
